import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as api from '../services/api'
import type { ChallengeAnswer } from '../types'

export function useHunts(params?: {
  lat?: number
  lon?: number
  radius?: number
  difficulty?: string
  search?: string
  sort?: 'distance' | 'newest' | 'popular'
  page?: number
  per_page?: number
}) {
  return useQuery({
    queryKey: ['hunts', params],
    queryFn: () => api.getHunts(params),
  })
}

export function useHunt(huntId: string) {
  return useQuery({
    queryKey: ['hunt', huntId],
    queryFn: () => api.getHunt(huntId),
    enabled: !!huntId,
  })
}

export function useStartHunt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (huntId: string) => api.startHunt(huntId),
    onSuccess: (_, huntId) => {
      queryClient.invalidateQueries({ queryKey: ['hunt', huntId] })
    },
  })
}

export function useWaypoints(huntId: string) {
  return useQuery({
    queryKey: ['waypoints', huntId],
    queryFn: () => api.getWaypoints(huntId),
    enabled: !!huntId,
  })
}

export function useCheckin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      waypointId,
      location,
    }: {
      waypointId: string
      location: { latitude: number; longitude: number; accuracy?: number }
    }) => api.checkinWaypoint(waypointId, location),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waypoints'] })
      queryClient.invalidateQueries({ queryKey: ['hunt'] })
    },
  })
}

export function useAnswerChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ waypointId, answer }: { waypointId: string; answer: ChallengeAnswer }) =>
      api.answerChallenge(waypointId, answer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waypoints'] })
      queryClient.invalidateQueries({ queryKey: ['hunt'] })
      queryClient.invalidateQueries({ queryKey: ['allBadges'] })
      queryClient.invalidateQueries({ queryKey: ['myBadges'] })
    },
  })
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
  })
}

export function useMyRewards() {
  return useQuery({
    queryKey: ['myRewards'],
    queryFn: api.getMyRewards,
  })
}

export function useMyBadges() {
  return useQuery({
    queryKey: ['myBadges'],
    queryFn: api.getMyBadges,
  })
}

export function useAllBadges() {
  return useQuery({
    queryKey: ['allBadges'],
    queryFn: api.getAllBadges,
  })
}

export function useMyHunts() {
  return useQuery({
    queryKey: ['myHunts'],
    queryFn: api.getMyHunts,
  })
}
